import { ft } from '../core/units.js';
import { EVENT } from './events.js';
import { RESCUES } from '../data/rescues.js';

/**
 * Somebody stranded, and getting them back.
 *
 * Posting one does the only two authored things there are: the guest stays
 * where they are, and one animal is put out near them. Everything after that
 * belongs to modules that already existed:
 *
 *   the animal     `wildlife.js`: it comes at anybody who gets close and goes
 *                  to see about a noise, and the golf cart is loud
 *   the lift       `riders.js`: anybody near the empty seat can be given one,
 *                  and a person in the back is still a person
 *   the complaint  `witnesses.js`: a guest who has something to say about it
 *                  says it to Management, as guests do
 *
 * This file watches and keeps the score: got them back to somewhere safe, the
 * animal got to them first, or nobody came.
 */

const clockOf = (clock) => clock.day * 1440 + clock.minutes;

export function createRescues({ npcs, wildlife, level, clock, bus, standing, hud, complain = () => {}, rows = RESCUES }) {
  let active = null;
  let records = [];

  const rowOf = (id) => rows.find((row) => row.id === id) ?? null;
  const guestFor = (row) => npcs.find((npc) => npc.data.strands === row.id) ?? null;
  const roomOf = (npc) => level.roomAt(npc.position.x, npc.position.z)?.id ?? null;

  function close(outcome) {
    const row = rowOf(active.id);
    const guest = guestFor(row);
    records.push({ id: active.id, day: active.day, outcome, at: clock.minutes });
    const changes = outcome === 'safe' ? row.award : outcome === 'hurt' ? row.hurt : row.left;
    const reason =
      outcome === 'safe'
        ? 'Got the guest back'
        : outcome === 'hurt'
          ? `The ${wildlife.creatures.find((c) => c.group === active.animal)?.definition.name.toLowerCase() ?? 'animal'} got to the guest first`
          : 'Nobody went out for the guest';
    standing?.applyAll(changes ?? {}, { reason });
    if (guest && outcome !== 'safe') {
      // They have something to say about it, and they say it to Management.
      complain({ npc: guest, record: { what: 'wildlife', detail: row.animal.id, subject: active.animal } });
    }
    if (guest?.posted) guest.unpost({ home: false });
    wildlife.dismiss(active.animal);
    active = null;
  }

  bus.on(EVENT.IMPACT, (event) => {
    if (!active || event.actor !== 'wildlife' || event.target?.type !== 'body') return;
    const row = rowOf(active.id);
    const guest = guestFor(row);
    if (!guest || event.target.id !== guest.data.id || event.kind !== row.animal.id) return;
    guest.say('Help!', 2.5);
    active.hurt = true;
  });

  return {
    rows,

    /** Somebody is out there. From a beat, when it is played. */
    post(id) {
      const row = rowOf(id);
      if (!row || active) return false;
      const guest = guestFor(row);
      if (!guest?.present) return false;
      const at = { x: ft(row.stranded[0]), z: ft(row.stranded[1]) };
      guest.post([{ ...at, dwell: 1e6 }], { from: true });
      const [animal] = wildlife.spawn(row.animal.id, { x: ft(row.animal.at[0]), y: 0, z: ft(row.animal.at[1]) }, { day: clock.day }) ?? [];
      active = { id, day: clock.day, from: clockOf(clock), animal: animal?.group ?? null, aboard: false, hurt: false };
      return true;
    },

    get active() {
      return active ? { ...active } : null;
    },

    get records() {
      return records.map((record) => ({ ...record }));
    },

    /** Zones the job you are on puts you in. */
    works(zone) {
      return Boolean(active && rowOf(active.id)?.works.includes(zone));
    },

    update() {
      if (!active) return;
      const row = rowOf(active.id);
      const guest = guestFor(row);
      if (!guest) {
        active = null;
        return;
      }
      if (active.hurt) return close('hurt');
      if (guest.aboard) {
        active.aboard = true;
        return;
      }
      // Out of the cart: back somewhere safe, or not yet.
      if (active.aboard) {
        active.aboard = false;
        if (row.safe.includes(roomOf(guest))) return close('safe');
      }
      if (clockOf(clock) - active.from >= row.lasts) return close('left');
    },

    toJSON: () => ({ active, records }),
    load(state) {
      active = null;
      records = (state?.records ?? []).map((record) => ({ ...record }));
    },
    clear() {
      active = null;
      records = [];
    },
  };
}
